import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { useGetExtraCostByShipment } from '@uturn/api/v1';
import {
	type Comment,
	// type ShipmentInvoiceDto,
	type ShipmentInvoiceLine,
	useGetInvoiceOfShipment,
} from '@uturn/api/v2';
import { LoadingSpan } from '@uturn/portal/components';
import { queryClient } from '@uturn/portal/query';
import { useAddShipmentStore } from '@uturn/portal/store/shipments/add-shipment';
import { formatPrice } from '@uturn/portal/utils';
import {
	Button,
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
	Icon,
	ScrollArea,
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from '@uturn/ui-kit';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdditionalCost } from './additional-cost';

export function Financials({ shipmentId }: { shipmentId: string }) {
	const shipmentIdNumber = Number(shipmentId);
	const { t } = useTranslation();

	const [hasNonApprovedCosts, setHasNonApprovedCosts] = useState(false);

	const { currencyCode } = useAddShipmentStore();
	const {
		data: extraCost,
		queryKey: extraCostQueryKey,
		isPending: isExtraCostPending,
	} = useGetExtraCostByShipment(shipmentIdNumber, {
		query: {
			retry: 1,
			select: (data) => data.data,
		},
	});

	const {
		data: invoice,
		queryKey: invoiceQueryKey,
		isPending: isInvoicePending,
	} = useGetInvoiceOfShipment(shipmentIdNumber, {
		query: {
			retry: 1,
			select: (data) => data.data,
		},
	});

	useEffect(() => {
		if (extraCost && extraCost.length > 0) {
			setHasNonApprovedCosts(
				extraCost?.some((cost) => cost.status?.code !== 'APPROVED'),
			);
		}
	}, [extraCost]);

	if (
		extraCost &&
		extraCost.length === 0 &&
		invoice?.invoiceLines?.length === 0
	) {
		return <div className="grid gap-7 p-3">No financials available</div>;
	}

	const invalidateQueries = () => {
		queryClient.invalidateQueries({ queryKey: extraCostQueryKey });
		queryClient.invalidateQueries({ queryKey: invoiceQueryKey });
	};

	if (isInvoicePending || isExtraCostPending) {
		return <LoadingSpan />;
	}

	return (
		<ScrollArea className="size-full">
			<div className="grid gap-5 pt-2">
				{extraCost && extraCost.length > 0 && hasNonApprovedCosts && (
					<div className="grid gap-4">
						<h3 className="font-heading text-lg font-medium tracking-tight">
							{t(
								'pages.shipment-detail.sheet.financials.additional-costs.title',
								'Additional costs',
							)}
						</h3>

						{extraCost.map((cost) => {
							if (cost.status?.code === 'APPROVED') return null;
							return (
								<AdditionalCost
									key={cost.id}
									cost={cost}
									currencyCode={currencyCode}
									invalidateQueries={invalidateQueries}
								/>
							);
						})}
					</div>
				)}

				{invoice?.invoiceLines && invoice.invoiceLines.length > 0 && (
					<div className="flex flex-col gap-4 pb-10">
						<div>
							<h3 className="font-heading text-lg font-medium tracking-tight">
								{t(
									'pages.shipment-detail.sheet.financials.invoice-lines.title',
									'Invoice lines',
								)}
							</h3>
							<p className="text-muted-foreground text-sm">
								{/* TODO: Add translation */}
								Invoice number -{' '}
								{invoice.invoiceNumber !== 0
									? invoice.invoiceNumber
									: t('general.invoice-to-be-invoiced', 'To be invoiced')}
							</p>
							<p className="text-muted-foreground text-sm">
								{/* TODO: Add translation */}
								Tax code - {invoice.invoiceLines[0].tax.rate ?? '-'}%{' '}
								{t(`general.tax-codes.${invoice.invoiceLines[0].tax.code}`, '')}
							</p>
						</div>

						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-5/12">
										{t(
											'pages.shipment-detail.sheet.financials.invoice-lines.description',
											'Description',
										)}
									</TableHead>
									<TableHead>
										{t(
											'pages.shipment-detail.sheet.financials.invoice-lines.status',
											'Status',
										)}
									</TableHead>
									<TableHead className="text-right">
										{t(
											'pages.shipment-detail.sheet.financials.invoice-lines.total',
											'Total',
										)}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{invoice.invoiceLines?.map((line: ShipmentInvoiceLine) => (
									<Collapsible key={line.reference} asChild>
										<>
											<TableRow>
												<TableCell className="flex font-medium">
													{line.description}
													{line.comments.length > 0 && (
														<CollapsibleTrigger asChild>
															<Button
																variant="ghost"
																size="sm"
																className="h-5 cursor-pointer px-1"
																asChild
															>
																<Icon icon={faChevronDown} className="size-3" />
															</Button>
														</CollapsibleTrigger>
													)}
												</TableCell>
												<TableCell>
													{t(`general.invoice-status.${line.lineStatus}`)}
												</TableCell>
												<TableCell>
													<div className="flex h-full items-center justify-end ">
														{formatPrice(
															Math.round(
																(line.amount.subtotal.quantity +
																	line.amount.vat.quantity +
																	Number.EPSILON) *
																	100,
															) / 100,
															currencyCode,
														) ?? '-'}
													</div>
												</TableCell>
											</TableRow>
											<CollapsibleContent asChild>
												<>
													{line.comments.length > 0 &&
														line.comments.map(
															(comment: Comment, index: number) => (
																<TableRow key={index} className="py-4">
																	<TableCell
																		colSpan={1}
																		className="font-medium"
																	>
																		{/* TODO: add translation query */}
																		{t(
																			`general.actor-type.${
																				comment.commentedBy ?? ''
																			}`,
																		)}{' '}
																		comment
																	</TableCell>
																	<TableCell colSpan={3} className="text-right">
																		{comment.text ?? '-'}
																	</TableCell>
																</TableRow>
															),
														)}
												</>
											</CollapsibleContent>
										</>
									</Collapsible>
								))}
							</TableBody>
							<TableFooter>
								<TableRow>
									<TableCell colSpan={2}>
										{t(
											'pages.shipment-detail.sheet.financials.invoice-lines.subtotal',
											'Subtotal',
										)}
									</TableCell>
									<TableCell className="text-right">
										{formatPrice(
											invoice.amount?.subtotal.quantity ?? 0,
											currencyCode,
										) ?? '-'}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={2}>
										{t(
											'pages.shipment-detail.sheet.financials.invoice-lines.vat',
											'VAT',
										)}
									</TableCell>
									<TableCell className="text-right">
										{formatPrice(
											invoice.amount?.vat.quantity ?? 0,
											currencyCode,
										) ?? '-'}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={2}>
										{t(
											'pages.shipment-detail.sheet.financials.invoice-lines.total-incl-vat',
											'Total incl. VAT',
										)}
									</TableCell>
									<TableCell className="text-right">
										{formatPrice(
											(invoice.amount?.subtotal.quantity ?? 0) +
												(invoice.amount?.vat.quantity ?? 0),
											currencyCode,
										) ?? '-'}
									</TableCell>
								</TableRow>
							</TableFooter>
						</Table>
					</div>
				)}
			</div>
		</ScrollArea>
	);
}
